import styles from "./ContentAnchors.module.scss";
import {
  Accordion,
  AccordionButton,
  AccordionPanel,
} from "components/utils/Accordion/Accordion";
import IconMS from "../../utils/IconMS/IconMS";
import useEscape from "../../../utils/useEscape";
import classNames from "classnames";
import { MouseEvent, useEffect, useRef, useState } from "react";
import ContentAnchorsHeading from "./ContentAnchorHeading";

interface AnchorLink {
  mainLink: {
    text?: string | null;
    url?: string;
  };
}

interface ScrollEvent {
  (event: MouseEvent, targetId: string | undefined): void;
}

export interface ContentAnchorsProps {
  heading?: string;
}

const ContentAnchors: React.FC<ContentAnchorsProps> = ({
  heading = "På denne siden",
}) => {
  const [linkList, setLinkList] = useState<AnchorLink[]>([]);
  const [offsetHeight, setOffsetHeight] = useState(0);
  const [hasScrolledPast, setHasScrolledPast] = useState(false);
  const [shouldClose, setShouldClose] = useState(false);
  const navContainerRef = useRef<HTMLDivElement>(null);
  const accordionButtonRef = useRef<HTMLButtonElement>(null);
  const isOpenRef = useRef(false);

  const [headerOffsetHeight, setHeaderOffsetHeight] = useState(0);

  useEffect(() => {
    const extractedLinkList = generateLinkList();
    setLinkList(extractedLinkList);
  }, []);

  //setting the header offset height and listening
  useEffect(() => {
    const header = document.querySelector("header");
    if (!header) return;

    const observer = new ResizeObserver(() => {
      setHeaderOffsetHeight(header.offsetHeight);
    });
    observer.observe(header);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const handleResize = (event: CustomEvent<{ headerHeight: number }>) => {
      setOffsetHeight(prev => {
        return event?.detail?.headerHeight;
      });
    };

    document.addEventListener("headerResize", handleResize);
    return () => {
      document.removeEventListener("headerResize", handleResize);
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (!navContainerRef.current) return;
      const rect = navContainerRef.current.getBoundingClientRect();
      const height = navContainerRef.current.offsetHeight;
      const isAboveViewport = rect.top + (isOpenRef.current ? height : 0) < 0;

      if (isAboveViewport && !hasScrolledPast) {
        setShouldClose(true);
        setHasScrolledPast(true);
      } else if (rect.top > offsetHeight && hasScrolledPast) {
        setHasScrolledPast(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [offsetHeight]);

  const handleClose = () => setShouldClose(true);

  const handleScroll: ScrollEvent = (event, targetId) => {
    event.preventDefault();
    handleClose();
    if (!targetId) return;

    const id = targetId.substring(1);
    const target = document.getElementById(id);
    if (!target) return;

    const targetRect = target.getBoundingClientRect();
    const movingDownPage = targetRect.top > 0;

    const headerAdjustment = movingDownPage ? 0 : headerOffsetHeight || 0;

    const targetTop = targetRect.top + window.scrollY;
    const scrollPosition = targetTop - headerAdjustment - 100;

    const navHeight =
      navContainerRef.current && !hasScrolledPast
        ? navContainerRef.current.offsetHeight
        : 0;

    if (!hasScrolledPast) {
      window.scrollTo({
        top: targetTop - navHeight - headerOffsetHeight,
        behavior: "smooth",
      });
    } else {
      window.scrollTo({
        top: scrollPosition,
        behavior: "smooth",
      });
    }
  };

  useEscape(handleClose);

  return (
    <div ref={navContainerRef}>
      <nav
        className={classNames(styles.contentAnchors, {
          [styles.isFixed]: hasScrolledPast,
        })}
        style={{
          maxHeight: hasScrolledPast
            ? `calc(100vh - ${offsetHeight}px)`
            : "unset",
          top: hasScrolledPast ? `${offsetHeight}px` : "0",
          width: hasScrolledPast
            ? `${navContainerRef.current?.offsetWidth}px`
            : "unset",
        }}
        id="content-anchor"
      >
        <Accordion isGlobal>
          <AccordionButton
            className={classNames(styles.header)}
            buttonRef={accordionButtonRef}
            isGlobal
          >
            {(isOpen, toggleAccordion) => {
              isOpenRef.current = isOpen;
              return (
                <ContentAnchorsHeading
                  heading={heading}
                  isOpen={isOpen}
                  shouldClose={shouldClose}
                  setShouldClose={setShouldClose}
                  toggleAccordion={toggleAccordion}
                />
              );
            }}
          </AccordionButton>

          <AccordionPanel className={styles.panel} id={heading}>
            <ul className={styles.linkList}>
              {linkList.map(({ mainLink }) =>
                mainLink?.url ? (
                  <li className={styles.item} key={mainLink.url}>
                    <IconMS
                      className={styles.iconArrow}
                      name="arrow_right"
                      size="20px"
                    />
                    <a
                      href={mainLink.url}
                      className={styles.itemLink}
                      onClick={event => handleScroll(event, mainLink.url)}
                    >
                      {mainLink.text}
                    </a>
                  </li>
                ) : null
              )}
            </ul>
          </AccordionPanel>
        </Accordion>
      </nav>
    </div>
  );
};

function generateLinkList(): AnchorLink[] {
  const contentBlocks = (document.querySelector("#page-level-content-area")
    ?.childNodes || []) as NodeListOf<HTMLElement>;

  const headings: HTMLElement[] = [];

  [...contentBlocks].forEach(block => {
    const h2s = block.querySelectorAll("h2");
    headings.push(...h2s);
  });

  return headings.map(h2 => {
    const textToLink =
      h2 && h2.textContent
        ? h2.textContent
            .toLowerCase()
            .trim()
            .replace(/[^\w\s-]/g, "") // Remove non-word characters except space/hyphen
            .replace(/\s+/g, "-") // Replace spaces with hyphens
        : "#";

    if (!h2.id) {
      h2.id = textToLink;
    }

    return {
      mainLink: {
        text: h2.textContent,
        url: `#${h2.id}`,
        anchorUrl: `${window.location.origin}${window.location.pathname}/#${textToLink}`,
      },
    };
  });
}

export default ContentAnchors;
